/**
 * OmniClinicalBridge PWA Update Notification
 * OmniAlera Technologies Holdings Inc.
 * 
 * Notifies users when a new version is available
 */

import React, { useState, useEffect } from 'react';
import { RefreshCw, X, Sparkles } from 'lucide-react';
import { skipWaiting } from '../pwa/registerSW';

export const PWAUpdateNotification: React.FC = () => {
  const [showUpdate, setShowUpdate] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  
  useEffect(() => {
    const handleSwUpdate = () => {
      setShowUpdate(true);
    };
    
    window.addEventListener('swUpdate', handleSwUpdate);
    
    return () => {
      window.removeEventListener('swUpdate', handleSwUpdate);
    };
  }, []);
  
  const handleUpdate = () => {
    setIsUpdating(true);
    skipWaiting();
  };
  
  const handleDismiss = () => {
    setShowUpdate(false);
  };
  
  if (!showUpdate) return null;
  
  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-down">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden max-w-sm">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-white" />
            <span className="text-white font-semibold">Update Available</span>
          </div>
          <button
            onClick={handleDismiss}
            className="text-white/80 hover:text-white transition-colors"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-4">
          <p className="text-gray-600 text-sm mb-4">
            A new version of OmniClinicalBridge is available with improved features and bug fixes.
          </p>
          
          <div className="flex gap-2">
            <button
              onClick={handleUpdate}
              disabled={isUpdating}
              className="flex-1 bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-400 text-white font-medium py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${isUpdating ? 'animate-spin' : ''}`} />
              {isUpdating ? 'Updating...' : 'Update Now'}
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-2.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAUpdateNotification;
